import React, { FC, useEffect, useState } from 'react';
import { UserInfo } from '../../utils/interfaces';
import {
  LocationResult,
  LocationSearchData,
  LocationsList,
  LocationType,
} from '../../utils/types';
import Pagination from '../shared/pagination';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
} from '@material-tailwind/react';
import PlacesModal from '../shared/places/placesModal';
import { LocationEnum, TeamMemberType } from '../../utils/enums';
import { Menu } from '@headlessui/react';
import { BiTrashAlt, BiPencil } from 'react-icons/bi';
import Toast from '../shared/toast';
import * as Sentry from '@sentry/nextjs';

type Props = {
  userInfo: UserInfo;
  locations?: LocationsList;
  locationParams?: LocationSearchData;
  setLocationParams?: React.Dispatch<
    React.SetStateAction<LocationSearchData | undefined>
  >;
  setRefreshSearch: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSearch: boolean;
};

const Locations: FC<Props> = ({
  userInfo,
  locations,
  locationParams,
  setLocationParams,
  setRefreshSearch,
  refreshSearch,
}) => {
  const [showPlaceModal, setPlaceModal] = useState<boolean>(false);
  const [newPlace, setNewPlace] = useState<boolean>(false);
  const [locationType, setLocationType] = useState<LocationType>(
    LocationEnum.PersonalPhysical
  );
  const [placeId, setPlaceId] = useState<number>(0);
  const [show, setShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const handleOpenHelpDialog = () => setOpenHelpDialog(!openHelpDialog);
  const [deleteParams, setDeleteParams] = useState<
    | {
        placeId: number;
        locationType: string;
        isTeamLocation: boolean;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    setRefreshSearch(!refreshSearch);
  }, [showPlaceModal]);

  if (userInfo === undefined) {
    return <>Loading</>;
  }

  const handleNext = () => {
    if (locationParams !== undefined && setLocationParams !== undefined) {
      setLocationParams({
        ...locationParams,
        PageNumber: locationParams?.PageNumber + 1,
      });
    }
  };

  const handlePrev = () => {
    if (locationParams !== undefined && setLocationParams !== undefined) {
      setLocationParams({
        ...locationParams,
        PageNumber: locationParams?.PageNumber - 1,
      });
    }
  };

  const searchByName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (locationParams !== undefined && setLocationParams !== undefined) {
      setLocationParams({
        ...locationParams,
        NameFilter: event.target.value,
      });
    }
  };

  const canEdit = (location: LocationResult) => {
    if (
      location.teamLocation &&
      userInfo.accountDetails?.teamMemberType === TeamMemberType.user
    ) {
      return false;
    }
    return true;
  };

  async function editPlace(
    newPlaceId: number,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    type: string,
    teamLocation: boolean
  ) {
    setNewPlace(false);
    if (type === 'Physical') {
      setLocationType(
        teamLocation ? LocationEnum.TeamPhysical : LocationEnum.PersonalPhysical
      );
      setPlaceId(newPlaceId);
      setPlaceModal(true);
    } else if (type === 'Virtual') {
      setLocationType(
        teamLocation ? LocationEnum.TeamVirtual : LocationEnum.PersonalVirtual
      );
      setPlaceId(newPlaceId);
      setPlaceModal(true);
    }
  }

  async function newLocation(type: LocationType) {
    setNewPlace(true);
    setPlaceId(0);
    setLocationType(type);
    setPlaceModal(true);
  }

  const deletePlace = async () => {
    console.log(deleteParams);
    if (deleteParams) {
      if (
        deleteParams.locationType === 'Physical' &&
        !deleteParams.isTeamLocation
      ) {
        try {
          await fetch(`/api/places/deactivatePlace/${deleteParams.placeId}`, {
            method: 'POST',
            body: JSON.stringify(deleteParams.placeId),
          }).then(async (result) => {
            if (!result.ok) {
              const errorBody = await result.text();
              throw new Error(
                `API call failed with status ${result.status}: ${errorBody}`
              );
            }
            if (result.status === 200) setRefreshSearch(!refreshSearch);
            setAlertMessage('Physical Location Removed');
            setShow(true);
          });
        } catch (error) {
          Sentry.captureException(error);
          return error;
        }
      } else if (
        deleteParams.locationType === 'Virtual' &&
        !deleteParams.isTeamLocation
      ) {
        const virtualLocationId = deleteParams.placeId;
        try {
          await fetch(`/api/virtualLocations/delete/${virtualLocationId}`, {
            method: 'DELETE',
            body: JSON.stringify(deleteParams.placeId),
          }).then(async (result) => {
            if (!result.ok) {
              const errorBody = await result.text();
              throw new Error(
                `API call failed with status ${result.status}: ${errorBody}`
              );
            }
            if (result.status === 200) setRefreshSearch(!refreshSearch);
            setAlertMessage('Virtual Location Removed');
            setShow(true);
          });
        } catch (error) {
          Sentry.captureException(error);
          return error;
        }
      } else if (
        deleteParams.locationType === 'Physical' &&
        deleteParams.isTeamLocation
      ) {
        try {
          await fetch(
            `/api/teams/${userInfo.accountDetails?.teamId}/places/delete/${deleteParams.placeId}`,
            {
              method: 'DELETE',
              body: JSON.stringify(deleteParams.placeId),
            }
          ).then(async (result) => {
            if (!result.ok) {
              const errorBody = await result.text();
              throw new Error(
                `API call failed with status ${result.status}: ${errorBody}`
              );
            }
            if (result.status === 200) setRefreshSearch(!refreshSearch);
            setAlertMessage('Team Physical Location Removed');
            setShow(true);
          });
        } catch (error) {
          Sentry.captureException(error);
          return error;
        }
      } else if (
        deleteParams.locationType === 'Virtual' &&
        deleteParams.isTeamLocation
      ) {
        const virtualLocationId = deleteParams.placeId;
        try {
          await fetch(
            `/api/teams/${userInfo.accountDetails?.teamId}/virtuallocations/delete/${virtualLocationId}`,
            {
              method: 'DELETE',
              body: JSON.stringify(deleteParams.placeId),
            }
          ).then(async (result) => {
            if (!result.ok) {
              const errorBody = await result.text();
              throw new Error(
                `API call failed with status ${result.status}: ${errorBody}`
              );
            }
            if (result.status === 200) setRefreshSearch(!refreshSearch);
            setAlertMessage('Team Virtual Location Removed');
            setShow(true);
          });
        } catch (error) {
          Sentry.captureException(error);
          return error;
        }
      }
    }
    handleOpenHelpDialog();
  };

  const handlePlaceModalClose = (
    newLocationId?: number,
    isVirtual?: boolean
  ) => {
    setPlaceModal(false);
  };

  return (
    <div>
      <Dialog
        open={openHelpDialog}
        handler={handleOpenHelpDialog}
        size={'xl'}
        className="overflow-auto h-auto max-h-[90%]"
      >
        <DialogHeader>Delete Location</DialogHeader>
        <DialogBody>Are you sure you want to delete this Location?</DialogBody>
        <DialogFooter>
          <Button
            className="mx-2"
            variant="gradient"
            color="teal"
            onClick={handleOpenHelpDialog}
          >
            <span>Cancel</span>
          </Button>
          <Button
            className="mx-2"
            variant="gradient"
            color="red"
            onClick={async () => await deletePlace()}
          >
            <span>Delete</span>
          </Button>
        </DialogFooter>
      </Dialog>
      <div className="absolute right-0 m-5">
        <Toast show={show} setShow={setShow} alertMessage={alertMessage} />
      </div>
      <div className="grid w-1/2">
        <PlacesModal
          showPlaceModal={showPlaceModal}
          setPlaceModal={handlePlaceModalClose}
          newLocation={newPlace}
          locationType={locationType}
          placeId={placeId}
          setShow={setShow}
          setAlertMessage={setAlertMessage}
        />
      </div>
      <div className="flex flex-col">
        <div className="flex flex-row w-full p-8 justify-between">
          <Input
            variant="outlined"
            type="text"
            label="Search Locations By Name"
            value={locationParams?.NameFilter}
            onChange={searchByName}
            className=" max-w-lg"
          />
          <div className="inline-flex justify-end">
            <Menu>
              <Menu.Button className=" rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 w-40">
                New Location
              </Menu.Button>
              <Menu.Items className="absolute mt-10 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="px-1 py-1">
                  <Menu.Item>
                    <Button
                      onClick={() => newLocation(LocationEnum.PersonalPhysical)}
                      variant="text"
                      className="text-confirmed-secondary w-full"
                    >
                      Physical Location
                    </Button>
                  </Menu.Item>
                  <Menu.Item>
                    <Button
                      onClick={() => newLocation(LocationEnum.PersonalVirtual)}
                      variant="text"
                      className="text-confirmed-secondary w-full"
                    >
                      Virtual Location
                    </Button>
                  </Menu.Item>{' '}
                  {userInfo.accountDetails?.teamMemberType !==
                    TeamMemberType.user && (
                    <>
                      <Menu.Item>
                        <Button
                          onClick={() => newLocation(LocationEnum.TeamPhysical)}
                          variant="text"
                          className="text-confirmed-secondary w-full"
                        >
                          Team Physical Location
                        </Button>
                      </Menu.Item>
                      <Menu.Item>
                        <Button
                          onClick={() => newLocation(LocationEnum.TeamVirtual)}
                          variant="text"
                          className="text-confirmed-secondary w-full"
                        >
                          Team Virtual Location
                        </Button>
                      </Menu.Item>
                    </>
                  )}
                </div>
              </Menu.Items>
            </Menu>
          </div>
        </div>
        <div className=" bg-white border-2 rounded-lg border-light-grey">
          <div className="flex flex-row p-4 w-full bg-light-grey">
            <div className="w-1/6">Type</div>
            <div className="w-2/6">Location Name</div>
            <div className="w-2/6">Team Location?</div>
          </div>
          <div className="divide-y-2">
            {locations?.locations?.map((location) => (
              <div key={location.id} className="flex flex-row p-4 w-full ">
                <div className="w-1/6">
                  <span>{location.type}</span>
                </div>
                <div className="w-2/6">
                  <span>{location.name}</span>
                </div>
                <div className="w-1/6">
                  <span>{isTeamLocation(location.teamLocation)}</span>
                </div>
                <div className="w-2/6">
                  {' '}
                  {canEdit(location) && (
                    <div className=" flex flex-row flex-wrap text-right text-blue-700 justify-around">
                      <button
                        className="flex flex-row flex-wrap items-center gap-2"
                        onClick={(e) =>
                          editPlace(
                            location.id,
                            e,
                            location.type,
                            location.teamLocation
                          )
                        }
                      >
                        <BiPencil></BiPencil>
                        Edit
                      </button>
                      <button
                        className="flex flex-row flex-wrap items-center text-red-700 gap-2"
                        onClick={() => {
                          handleOpenHelpDialog();
                          setDeleteParams({
                            ...deleteParams,
                            placeId: location.id,
                            locationType: location.type,
                            isTeamLocation: location.teamLocation,
                          });
                        }}
                      >
                        <BiTrashAlt className="inline"></BiTrashAlt>{' '}
                        <span>Delete</span>
                      </button>
                    </div>
                  )}{' '}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Pagination
          currentPage={locationParams?.PageNumber}
          resultsPerPage={locationParams?.ResultsPerPage}
          totalResults={locations?.totalRecords}
          handleNext={handleNext}
          handlePrev={handlePrev}
        ></Pagination>
      </div>
    </div>
  );
};

export default Locations;
const isTeamLocation = (teamLocation: boolean) => {
  return teamLocation ? 'Yes' : 'No';
};
